"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const check_1 = require("../common/check");
const geom_1 = require("../common/geom");
const CreaseEdgeLoop_1 = require("./CreaseEdgeLoop");
/**
 * A face in a Crease object.
 */
class CreaseFace {
    /**
     * Constructs a CreaseFace object.
     * @param {number} index The index of this face.
     * @param {CreaseEdgeLoop[]} edgeLoops An array of edge loops.
     * @param {string} [category] The category of this face.
     */
    constructor(index, edgeLoops, category) {
        // Check inputs.
        check_1.check.isInteger('index', index);
        check_1.check.isArray('edgeLoops', edgeLoops, [1, Infinity], (name, edgeLoop) => check_1.check.isInstanceOf(name, edgeLoop, CreaseEdgeLoop_1.CreaseEdgeLoop));
        this._index = index;
        this._edgeLoops = edgeLoops;
        this._category = category;
    }
    /**
     * Returns the index of this face.
     * @returns {number} The index of this face.
     */
    get index() {
        return this._index;
    }
    /**
     * Returns an array of edge loops. The first loop defines the outer boundary of this face, and
     * subsequent loops define holes in this face.
     * @returns {CreaseEdgeLoop[]} An array of edge loops.
     */
    get edgeLoops() {
        return this._edgeLoops;
    }
    /**
     * Returns an array, each entry of which is an array of vertices representing a closed loop.
     * The first loop defines the outer boundary of this face, and subsequent loops define holes in
     * this face.
     * @returns {CreaseVertex[][]} An array of loops of vertices.
     */
    get vertexLoops() {
        return this.edgeLoops.map(edgeLoop => edgeLoop.vertices);
    }
    /**
     * Returns the category of this face.
     * @returns {string | undefined} The category of this face.
     */
    get category() {
        return this._category;
    }
    /**
     * Sets the category of this face.
     * @param {string | undefined} category The category to use.
     */
    set category(category) {
        this._category = category;
    }
    /**
     * Returns the area of this face. Treats curved edges as if they were straight, for now.
     * @returns {number} The area of this face.
     */
    get area() {
        let area = 0;
        this.vertexLoops.forEach(loop => {
            const positions = loop.map(vertex => vertex.position);
            area += geom_1.geom.getSignedArea(positions);
        });
        return area;
    }
    /**
     * Returns an array of faces that are incident to this one.
     * @returns {CreaseFace[]} An array of faces that are incident to this one.
     */
    get incidentFaces() {
        const incidentFaces = [];
        this.edgeLoops.forEach((edgeLoop) => {
            edgeLoop.edgeRefs.forEach((edgeRef) => {
                const otherFace = edgeRef.rightFace;
                if (otherFace && incidentFaces.indexOf(otherFace) < 0) {
                    incidentFaces.push(otherFace);
                }
            });
        });
        return incidentFaces;
    }
    /**
     * Sets the index of this face. Used internally.
     */
    _setIndex(index) {
        this._index = index;
    }
    /**
     * Returns the edge reference used by this face to refer to the given edge, if any.
     * @param {CreaseEdge} edge An edge.
     * @returns {CreaseEdgeRef | undefined} The edge reference used by this face to refer to the
     * given edge, or undefined.
     */
    findEdgeRef(edge) {
        for (let i = 0; i < this.edgeLoops.length; i++) {
            const edgeRef = this.edgeLoops[i].findEdgeRef(edge);
            if (edgeRef) {
                return edgeRef;
            }
        }
        return undefined;
    }
    /**
     * Returns an object that can be serialized using `JSON.stringify`.
     * @returns {CreaseJSONFaceV1} The serializable representation of this face.
     */
    toJSON() {
        const json = { edgeLoops: this.edgeLoops.map(loop => loop.toJSON()) };
        if (this.category) {
            json.category = this.category;
        }
        return json;
    }
    /**
     * Checks deep equality between this and another CreaseFace object
     * @param {CreaseFace} face A face.
     * @param {boolean} [logDebug = false] A flag to log unequal elements, default to false.
     * @returns {boolean} Returns true if equal, else false.
     */
    equals(face, logDebug = false) {
        let equal = true;
        const { edgeLoops, index } = this;
        face.edgeLoops.forEach((loops, i) => {
            loops.edgeRefs.forEach((edgeRef, j) => {
                const edgeRef2 = edgeLoops[i].edgeRefs[j];
                if (edgeRef.edge.index !== edgeRef2.edge.index
                    || edgeRef.isReversed !== edgeRef2.isReversed) {
                    if (logDebug) {
                        /* eslint-disable-next-line no-console */
                        console.log(`Face at index ${index} have unequal edges at ${JSON.stringify([i, j])}:
                        ${edgeRef2.toJSON()}, ${edgeRef.toJSON()}`);
                    }
                    equal = false;
                }
            });
        });
        // We could add more here if needed.
        return equal;
    }
}
exports.CreaseFace = CreaseFace;
//# sourceMappingURL=CreaseFace.js.map